import {isFeatureEnabled} from 'feature-flags';
import {Card, DataStatus, Table, TableRow} from 'platform/components';
import {Box, Link, Show, Text, VStack} from 'platform/foundation';

import {head, isNotNil, sort} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  AdvertisementStatusEnum,
  useGetAdvertisementPreflightQuery,
  useGetGeneralSettingsQuery,
  useGetVehicleQuery,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum, EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {EMPTY_PLACEHOLDER, fakePlatforms, platformImageURLs, usePermissions} from '@dms/shared';

import {getSafely, parseDate} from 'shared/web';

import {useAdvertisementPlatformFilter} from '../hooks/useAdvertisementPlatformFilter';
import {AdditionalInfoStatus} from './AdditionalInfoStatus';
import {PlatformActions} from './PlatformActions';
import {PlatformDate} from './PlatformDate';
import {PlatformLogo} from './PlatformLogo';
import {PlatformPreflightStatus} from './PlatformPreflightStatus';
import {PlatformPrice} from './PlatformPrice';
import {PlatformSwitcher} from './PlatformSwitcher';
import {PlatformUpdatedBy} from './PlatformUpdatedBy';
import {StatusItem} from './StatusItem';

const EMPTY_COLUMN_HEADER = null;

const COLUMN_TITLES = [
  EMPTY_COLUMN_HEADER,
  i18n.t('page.advertisement.labels.platformName'),
  i18n.t('page.advertisement.labels.status'),
  i18n.t('page.advertisement.labels.url'),
  i18n.t('page.advertisement.labels.advertisedPrice'),
  i18n.t('page.advertisement.labels.data'),
  i18n.t('page.advertisement.labels.publishingBranch'),
  i18n.t('page.advertisement.labels.updatedBy'),
  i18n.t('page.advertisement.labels.updated'),
  EMPTY_COLUMN_HEADER,
] as const;

export interface AdPlatformProps {
  vehicleId: string;
  isPollingEnabled: boolean;
  canPreview?: boolean;
  onPreview?: (platformCode?: AdvertisingPlatformCodeEnum) => void;
}

export function AdPlatforms(props: AdPlatformProps) {
  const {data: generalSettings} = useGetGeneralSettingsQuery({vehicleId: props.vehicleId});
  const isAdvertisementPreflightEnabled = isFeatureEnabled(featureFlags.ADVERTISEMENT_PREFLIGHT);
  const {
    data: preflight,
    isFetching: isPreflightLoading,
    isError: isPreflightError,
  } = useGetAdvertisementPreflightQuery(
    {
      vehicleId: props.vehicleId,
      publishVgpViaAdvertisingBridge: isFeatureEnabled(
        featureFlags.ADVERTISEMENT_VGP_PROMPT_BRIDGE
      ),
      publishOtomotoViaAdvertisingBridge: isFeatureEnabled(
        featureFlags.ADVERTISEMENT_OTOMOTO_PROMPT_BRIDGE
      ),
      publishAutocarisViaAdvertisingBridge: isFeatureEnabled(
        featureFlags.ADVERTISEMENT_AUTOCARIS_PROMPT_BRIDGE
      ),
      publishMercedesNaSkladeViaAdvertisingBridge: isFeatureEnabled(
        featureFlags.ADVERTISEMENT_MERCEDES_NA_SKLADE_PROMPT_BRIDGE
      ),
      publishHyundaiPromiseViaAdvertisingBridge: isFeatureEnabled(
        featureFlags.ADVERTISEMENT_HYUNDAI_PROMISE_PROMPT_BRIDGE
      ),
    },
    {skip: !isAdvertisementPreflightEnabled}
  );
  // Undefined until the settings arrive; the switch must not read that as "not in sale".
  const inSale = generalSettings?.inSale;

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const [hasEnablePlatformForVehiclePermission, hasDisablePlatformForVehiclePermission] =
    usePermissions({
      permissionKeys: ['enablePlatformForVehicle', 'disablePlatformForVehicle'],
      scopes: {
        enablePlatformForVehicle: {
          participation: vehicleParticipation,
          branchId: vehicle?.branchId,
        },
        disablePlatformForVehicle: {
          participation: vehicleParticipation,
          branchId: vehicle?.branchId,
        },
      },
    });

  const isDisabled =
    !hasEnablePlatformForVehiclePermission || !hasDisablePlatformForVehiclePermission;

  const {
    platforms: filteredPlatforms,
    isLoading,
    isError,
    allPlatforms,
  } = useAdvertisementPlatformFilter({
    vehicleId: props.vehicleId,
    skipPolling: !props.isPollingEnabled,
  });

  const triggeredUpdates = allPlatforms?.map((platform) => platform.statusTriggeredAt) ?? [];
  const latestUpdate =
    head(
      sort(
        (a, b) => (parseDate(b)?.getTime() ?? 0) - (parseDate(a)?.getTime() ?? 0),
        triggeredUpdates
      )
    ) ?? '';
  const preflightByPlatformCode = new Map(
    preflight?.results.map((result) => [result.platformCode, result]) ?? []
  );

  const items = filteredPlatforms?.map((platformData) => {
    const code = platformData.platform.code;
    const platformTitle = platformData.platform.title;
    const status = platformData.status;
    const preflightResult = preflightByPlatformCode.get(code);
    // BE contract: 'ready' is the only non-blocking preflight status - fail closed on anything else.
    const isPreflightBlocking =
      isAdvertisementPreflightEnabled &&
      status === AdvertisementStatusEnum.NOT_PUBLISHED &&
      (isPreflightLoading ||
        (!isPreflightError && isNotNil(preflightResult) && preflightResult.status !== 'ready'));

    return (
      <TableRow key={code} data-testid={testIds.vehicles.publishing(`platform-${code}`)}>
        <Box paddingHorizontal={2}>
          <PlatformSwitcher
            code={code}
            value={platformData.enabled}
            isDisabled={isDisabled || isPreflightBlocking}
            inSale={inSale}
          />
        </Box>
        <PlatformLogo src={getSafely(platformImageURLs, code)} name={platformData.platform.title} />
        <VStack spacing={1} align="flex-start">
          <StatusItem
            currentStatus={platformData.status}
            publicationStatus={platformData.publicationStatus}
            statusDescription={platformData.statusDescription}
            platformCode={code}
          />
          <Show whenFeatureEnabled={featureFlags.ADVERTISEMENT_PREFLIGHT}>
            <PlatformPreflightStatus
              vehicleId={props.vehicleId}
              platformCode={code}
              platformTitle={platformTitle}
              status={preflightResult?.status}
              issues={preflightResult?.issues ?? []}
              isLoading={isPreflightLoading}
              isError={isPreflightError}
            />
          </Show>
        </VStack>
        <div>
          {isNilOrEmpty(platformData.url) ? (
            EMPTY_PLACEHOLDER
          ) : (
            <Link href={platformData.url} target="_blank" size="small">
              {i18n.t('page.advertisement.labels.show_ad')}
            </Link>
          )}
        </div>
        <PlatformPrice price={platformData.priceWithVat} />
        <div>
          {platformData.hasOverrides
            ? i18n.t('general.labels.custom')
            : i18n.t('general.labels.default')}
        </div>
        <div>
          {platformData.status === AdvertisementStatusEnum.NOT_PUBLISHED
            ? EMPTY_PLACEHOLDER
            : (platformData.publishingBranchName ?? i18n.t('entity.tenant.labels.tenant'))}
        </div>
        <PlatformUpdatedBy
          text={
            platformData.statusTriggeredBy ??
            (isNotNil(platformData.statusTriggeredAt)
              ? i18n.t('page.advertisement.labels.systemUser')
              : null)
          }
        />
        <PlatformDate platformCode={code} updatedAt={platformData.statusTriggeredAt} />
        <PlatformActions
          platformCode={code}
          vehicleId={props.vehicleId}
          currentStatus={status}
          platformTitle={platformTitle}
          inSale={inSale ?? false}
          statusTriggeredAt={platformData.statusTriggeredAt}
          canPreview={props.canPreview && platformData.enabled}
          onPreview={props.onPreview}
        />
      </TableRow>
    );
  });

  const fakeItems = fakePlatforms.map(({code, title}) => {
    const status = AdvertisementStatusEnum.NOT_PUBLISHED;

    return (
      <TableRow key={code} data-testid={testIds.vehicles.publishing(`platform-${code}`)}>
        <Box paddingHorizontal={2}>
          <PlatformSwitcher
            // @ts-ignore Fake platform code
            code={code}
            value={false}
            isDisabled
            inSale={inSale}
          />
        </Box>
        <PlatformLogo src={getSafely(platformImageURLs, code)} name={title} />
        <StatusItem
          currentStatus={AdvertisementStatusEnum.NOT_PUBLISHED}
          statusDescription={null}
          platformCode={code}
        />
        <div>{EMPTY_PLACEHOLDER}</div>
        <PlatformPrice price={null} />
        <div>{i18n.t('general.labels.default')}</div>
        <div>{EMPTY_PLACEHOLDER}</div>
        <PlatformUpdatedBy />
        <PlatformDate platformCode={code} />
        <PlatformActions
          // @ts-ignore Fake platform code
          platformCode={code}
          vehicleId={props.vehicleId}
          currentStatus={status}
          platformTitle={title}
          inSale={inSale ?? false}
          statusTriggeredAt={null}
        />
      </TableRow>
    );
  });

  return (
    <Card>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={!items?.length}
        emptyMessage={i18n.t('page.advertisement.labels.notAvailablePlatforms')}
        spacing={10}
      >
        <VStack spacing={4}>
          <AdditionalInfoStatus latestUpdate={latestUpdate} inAdvertisement={inSale ?? false} />
          <Box overflowX="auto">
            <Box minWidth={240}>
              <Table
                columns={COLUMN_TITLES.map((item) => ({
                  element: (
                    <Box paddingBottom={2}>
                      <Text key={item} size="xSmall" align="left">
                        {item}
                      </Text>
                    </Box>
                  ),
                }))}
                data-testid={testIds.vehicles.publishing(`platform-list`)}
              >
                {items}
                <Show whenFeatureEnabled={featureFlags.ADVERTISEMENT_FAKE_PLATFORMS}>
                  {fakeItems}
                </Show>
              </Table>
            </Box>
          </Box>
        </VStack>
      </DataStatus>
    </Card>
  );
}
